import { createContext, useCallback, useContext } from 'react';

import { useDocumentsUpload } from '../use-documents-upload';
import type { UploadedFile, UseUploadResult } from '../use-upload';

type FormUploadsStateContextValue = {
  isUploading: boolean;
  showPendingUploadError: () => void;
};

type DocumentsUploadContextValue = {
  uploads: UseUploadResult['uploads'];
  onDeleteUpload: UseUploadResult['handleDeleteUpload'];
  onUploadFile: UseUploadResult['handleUploadFile'];
  showUploadInProgressError: boolean;
  resetShowUploadInProgressError: () => void;
};

const ApplyFormUploadsStateContext = createContext<
  FormUploadsStateContextValue | undefined
>(undefined);
const CvDocUploadContext = createContext<
  DocumentsUploadContextValue | undefined
>(undefined);
const OtherDocsUploadContext = createContext<
  DocumentsUploadContextValue | undefined
>(undefined);

export const ApplyFormUploadsProvider: React.FC<
  React.PropsWithChildren<unknown>
> = ({ children }) => {
  const {
    uploads: cvUpload,
    isUploading: isCvUploading,
    handleUploadFile: handleCvUploadFile,
    handleDeleteUpload: handleCvDeleteUpload,
    showUploadInProgressError: showCvUploadInProgressError,
    setShowUploadInProgressError: setShowCvUploadInProgressError,
  } = useDocumentsUpload();

  const {
    uploads: otherDocsUpload,
    isUploading: isOtherDocsUploading,
    handleDeleteUpload: handleOtherDocsDeleteUpload,
    handleUploadFile: handleOtherDocsUploadFile,
    showUploadInProgressError: showOtherDocsUploadInProgressError,
    setShowUploadInProgressError: setShowOtherDocsUploadInProgressError,
  } = useDocumentsUpload();

  const showPendingUploadError = (): void => {
    if (isCvUploading) setShowCvUploadInProgressError(isCvUploading);
    if (isOtherDocsUploading) {
      setShowOtherDocsUploadInProgressError(isOtherDocsUploading);
    }
  };

  const resetShowCvUploadInProgressError = useCallback(
    () => setShowCvUploadInProgressError(false),
    [setShowCvUploadInProgressError]
  );

  const resetShowOtherDocsUploadInProgressError = useCallback(
    () => setShowOtherDocsUploadInProgressError(false),
    [setShowOtherDocsUploadInProgressError]
  );

  return (
    <ApplyFormUploadsStateContext.Provider
      value={{
        isUploading: isCvUploading || isOtherDocsUploading,
        showPendingUploadError,
      }}
    >
      <CvDocUploadContext.Provider
        value={{
          uploads: cvUpload,
          onUploadFile: handleCvUploadFile,
          onDeleteUpload: handleCvDeleteUpload,
          showUploadInProgressError: showCvUploadInProgressError,
          resetShowUploadInProgressError: resetShowCvUploadInProgressError,
        }}
      >
        <OtherDocsUploadContext.Provider
          value={{
            uploads: otherDocsUpload,
            onUploadFile: handleOtherDocsUploadFile,
            onDeleteUpload: handleOtherDocsDeleteUpload,
            showUploadInProgressError: showOtherDocsUploadInProgressError,
            resetShowUploadInProgressError:
              resetShowOtherDocsUploadInProgressError,
          }}
        >
          {children}
        </OtherDocsUploadContext.Provider>
      </CvDocUploadContext.Provider>
    </ApplyFormUploadsStateContext.Provider>
  );
};

export const useFormUploadsState = (): FormUploadsStateContextValue => {
  const formUploadsState = useContext(ApplyFormUploadsStateContext);

  if (typeof formUploadsState === 'undefined') {
    throw new Error(
      'useDocsUploadProps must be used within an ApplyFormUploadsContext'
    );
  }

  return formUploadsState;
};

export const useCvDocumentUploadProps = (): DocumentsUploadContextValue => {
  const docsUploadProps = useContext(CvDocUploadContext);

  if (typeof docsUploadProps === 'undefined') {
    throw new Error(
      'useCvDocumenUploadProps must be used within an ApplyFormUploadsContext'
    );
  }

  return docsUploadProps;
};

export const useOtherDocumentsUploadProps = (): DocumentsUploadContextValue => {
  const docsUploadProps = useContext(OtherDocsUploadContext);

  if (typeof docsUploadProps === 'undefined') {
    throw new Error(
      'useOtherDocumentsUploadProps must be used within an ApplyFormUploadsContext'
    );
  }

  return docsUploadProps;
};

export const useCvUploadFileNameById = (
  uploadId?: string
): string | undefined => {
  const cvUploadProps = useContext(CvDocUploadContext);

  if (uploadId) {
    return cvUploadProps?.uploads.find(
      (upload: UploadedFile) => upload?.id?.toString() === uploadId
    )?.fileName;
  }

  return undefined;
};
